import { Button, FormLabel, HStack, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from '@chakra-ui/react';
import React, { useState } from 'react';

import type { CustomPanelData } from './blueprint-custom-pages-context';

export type CustomPanelSizeDialogProps = {
	panel: CustomPanelData | null;
	onApply: (size: { width: number; height: number }) => void;
	onClose: () => void;
};

const toInches = (value: string, fallback: number): number => {
	const parsed = parseFloat(value);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/** Width/height editor for one free-standing custom panel. Mounted per page and keyed on the panel so it always opens with that panel's current size. */
export const CustomPanelSizeDialog = (props: CustomPanelSizeDialogProps): React.ReactElement => {
	const { panel, onApply, onClose } = props;

	const [width, setWidth] = useState(String(panel?.width ?? ''));
	const [height, setHeight] = useState(String(panel?.height ?? ''));

	const apply = (): void => {
		if (!panel) return;
		onApply({ width: toInches(width, panel.width), height: toInches(height, panel.height) });
		onClose();
	};

	return (
		<Modal isOpen={panel !== null} onClose={onClose} isCentered={true} size='xs'>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader fontSize='md'>Panel Size</ModalHeader>
				<ModalBody>
					<HStack spacing={3} align='flex-end'>
						<div>
							<FormLabel fontSize='sm' mb={1}>Width (in)</FormLabel>
							<Input size='sm' type='number' step='0.125' min='0.125' value={width} onChange={(e) => setWidth(e.target.value)} />
						</div>
						<div>
							<FormLabel fontSize='sm' mb={1}>Height (in)</FormLabel>
							<Input size='sm' type='number' step='0.125' min='0.125' value={height} onChange={(e) => setHeight(e.target.value)} />
						</div>
					</HStack>
				</ModalBody>
				<ModalFooter gap={2}>
					<Button size='sm' variant='ghost' onClick={onClose}>Cancel</Button>
					<Button size='sm' colorScheme='blue' onClick={apply}>Apply</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
